import {
  Component,
  ChangeDetectionStrategy,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { Platform, Nav } from 'ionic-angular';
import { StatusBar } from '@ionic-native/status-bar';
import { SplashScreen } from '@ionic-native/splash-screen';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { URLSearchParams } from '@angular/http';

import { Store } from '@ngrx/store';

import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

import {
  AssessmentService,
  AssessmentActions,
  ViewActions,
  ViewService,
  UserService,
  UserActions
} from './logics';

import {
  SelectionHandler,
  PageEventsHandler,
  SocketHandler,
  Utils,
  Translate
} from './services';

import {
  REQUEST_FULLSCREEN,
  FULLSCREEN_EXIT_ON_LOST_FOCUS,
  FULLSCREEN_EXIT_ON_MOUSE_OUT,
  FULLSCREEN_EXIT_ON_RESIZE,
  MOUSE_OUT_ALLOWED_WIHOUT_PULSE_SECS,
  MOUSE_OUT_ALLOWED_SECS,
  FULLSCREEN_EXIT_IN_SECS,
  CHECK_BEFORE_CLOSE,
  DISABLE_RIGHT_CLICK,
  DISABLE_SHORTCUT,
  DISABLE_CONSOLE,
  DISABLE_SELECTION,
  CHECK_DEV_TOOLS,
  SHOW_PULSE,
  RESIZE_TOLERANCE_IN_PX,
  FREE_INSERT_MODE,
  TRAINING_MODE,
  CISIA_CONSOLE_COMBINATION,
  EXTERNAL_URL_TO_CHECK_CONNECTION,
  EXTERNAL_URL_TO_CHECK_CONNECTION_TIMEOUT,
  PARTS_TO_CHECK,
  CS_F_VERSION,
  SYNC_FREQUENCY_SECS
} from './constants';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ion-nav [root]="rootPage"></ion-nav>
    <div 
      [ngClass]="{
        'cs-invert': invertColor
      }"
      [ngStyle]="{
        'zoom': zoom + '%',
        '-ms-zoom': zoom + '%'
      }">
      <div [ngSwitch]="viewName$ | async">
        <survey-layout
          *ngSwitchCase="'survey'"
          [shared]="{
            userData: (userData$ | async),
            timerData: {
              timeLeft: (timeLeft$ | async),
              timePercentage: (timePercentage$ | async),
              label: (translations$ | async)['survey#timer_label']
            }
          }"
          (viewChange)="onViewChange($event)"
          (expandImage)="onExpandImage($event)"
          (highContrastToggle)="onHighContrastToggle()"
          (increaseZoom)="increaseZoom()"
          (decreaseZoom)="decreaseZoom()"
          (openHelp)="onOpenHelp()">
        </survey-layout>
        
        <wait-layout
          *ngSwitchCase="'wait'"
          [shared]="shared"
          (viewChange)="onViewChange($event)">
        </wait-layout>
  
        <report-layout 
          *ngSwitchCase="'report'"
          [shared]="shared"
          (viewChange)="onViewChange($event)"
          (expandImage)="onExpandImage($event)"
          (highContrastToggle)="onHighContrastToggle()"
          (increaseZoom)="increaseZoom()"
          (decreaseZoom)="decreaseZoom()">
        </report-layout>
  
        <login-layout 
          *ngSwitchCase="'login'"
          (viewChange)="onViewChange($event)">
        </login-layout>
      </div>
  
      <cs-modal
        *ngIf="isModalVisible$ | async"
        [inputs]="(modal$ | async)"
        [translations]="{
          confirmLabel: (translations$ | async)['modal#confirm_label'],
          insertCodeLabel: (translations$ | async)['modal#insert_code_label'],
          noConnection: (translations$ | async)['modal#no_connection']
        }"
        (cancelRequest)="onCancelRequest()"
        (submitRequest)="onSubmitRequest($event)"
        (submitValidation)="onSubmitValidation($event)">
      </cs-modal>
  
      <cs-modal-image
        *ngIf="isModalImageVisible$ | async"
        [inputs]="(modalImage$ | async)"
        (close)="onModalImageClose()">
      </cs-modal-image>
  
      <cs-alert
        [inputs]="{
          alerts: alerts$ | async
        }"
        (close)="onAlertClose($event)">
      </cs-alert>
  
      <cs-help
        *ngIf="showHelp"
        (closeHelp)="onCloseHelp()"></cs-help>

      <div
        class="cs-modal-block-bg-w cs-simple-page"
        *ngIf="!(freezeView$ | async) && (canShowPulse$ | async) && isFullscreen && showPulse">
        <div class="cs-simple-page__content">
          <p class="cs-simple-page__text">
            {{ (translations$ | async)['global#lost_focus'] }}
          </p>
        </div>
      </div>

      <div 
        class="cs-modal-block-bg cs-pulse"
        *ngIf="!(freezeView$ | async) && (canShowPulse$ | async) && isFullscreen && showPulse">
      </div>
    </div>  
  `
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(Nav) nav: Nav;

  rootPage: any;
  shared: Object = {};
  translations: Object = {};
  canClose: boolean = true;
  closeRequest: boolean = false;
  isFullscreen: boolean = false;
  freeInsert: boolean = FREE_INSERT_MODE;
  trainingMode: boolean = TRAINING_MODE;
  showPulse: boolean = SHOW_PULSE;
  showHelp: boolean = false;
  invertColor: boolean = false;
  invertStyle: boolean = false;
  completed: boolean = false;
  isExposedOnInternet: boolean = false;
  isConnectedToServer: boolean = false;
  partsToCheck = PARTS_TO_CHECK;
  combo = CISIA_CONSOLE_COMBINATION;
  currentCombo = new Array(this.combo.length);
  zoom = 100;
  requestFullscreen: boolean;

  triggerTimer$: Observable<any>;
  currentSection$: Observable<any>;
  currentSectionTime$: Observable<any>;
  completed$: Observable<any>;
  exitFullscreen$: Observable<any>;
  userData$: Observable<any>;
  timeLeft$: Observable<any>;
  sectionTimeTerminated$: Observable<any>;
  timePercentage$: Observable<any>;
  translations$: Observable<any>;
  alerts$: Observable<any>;

  interruptExit$: Observable<boolean>;
  freezeView$: Observable<boolean>;
  canShowPulse$: Observable<boolean>;
  isSurveyCompleted$: Observable<boolean>;
  isModalVisible$: Observable<any>;
  isModalImageVisible$: Observable<any>;
  surveyView$: Observable<boolean>;

  viewName$: Observable<string>;
  modal$: Observable<Object>;
  modalImage$: Observable<Object>;
  destroyed$: Subject<any> = new Subject<any>();

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // TODO: update to angular >2.4.1 to use Observable here

    this.store.dispatch(this.assessmentActions.interruptAttemp());
    if (this.completed) {
      this.socketHandler.guiEventRequest('test#interruptAttemp');
    }
    this.closeRequest = true;
    return this.canClose;
  }

  constructor(
    public platform: Platform,
    public statusBar: StatusBar,
    public splashScreen: SplashScreen,
    public screenOrientation: ScreenOrientation,
    private store: Store<any>,
    private utils: Utils,
    private translate: Translate,
    private assessmentActions: AssessmentActions,
    private assessmentService: AssessmentService,
    private viewActions: ViewActions,
    private viewService: ViewService,
    private userService: UserService,
    private userActions: UserActions,
    private selectionHandler: SelectionHandler,
    private pageEventsHandler: PageEventsHandler,
    private socketHandler: SocketHandler,
  ) {
    platform.ready().then(() => {
      // Okay, so the platform is ready and our plugins are available.
      // Here you can do any higher level native things you might need.
      statusBar.styleDefault();
      splashScreen.hide();

      // set to landscape
      if(this.utils.isMobile()){
        this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.LANDSCAPE);
      }
    });

    this.requestFullscreen = REQUEST_FULLSCREEN && this.utils.isWeb();

    this.translations$ = this.translate.getTranslations$();
    this.alerts$ = this.viewService.getAlerts$();

    this.userData$ = this.userService.getUser$();
    this.isModalVisible$ = this.viewService.isModalVisible$();
    this.isModalImageVisible$ = this.viewService.isModalImageVisible$();
    this.modal$ = this.viewService.getModal$();
    this.modalImage$ = this.viewService.getModalImage$();
    this.completed$ = this.assessmentService.isCompleted$();
    this.currentSection$ = this.assessmentService.getCurrentSectionMetadata$();

    this.viewName$ = this.viewService
      .viewName$()
      .withLatestFrom(this.assessmentService.isLocked$())
      .map(([viewName, locked]) => {
        return locked
          ? 'wait'
          : viewName;
      })
      .debounceTime(200);

    this.surveyView$ = this.viewService.viewName$()
      .filter(viewName => viewName === 'survey')
      .mapTo(true);
    this.isSurveyCompleted$ = this.completed$
      .filter(complete => complete);

    this.triggerTimer$ = Observable.merge(
      this.currentSection$,
      this.assessmentService.isStarted$()
        .delayWhen(() => this.surveyView$)
        .filter((started: any) => started)
        .withLatestFrom(this.currentSection$)
        .map(([, section]) => section),
      this.assessmentService.resetCounterTrigger$()
        .delayWhen(() => this.surveyView$)
        .withLatestFrom(this.assessmentService.isStarted$())
        .filter((arr: any, ) => {
          return arr[1];
        })
        .withLatestFrom(this.currentSection$)
        .map(([, section]) => {
          return section;
        })
    )
      .debounceTime(10);

    this.currentSectionTime$ = this.triggerTimer$
      .skipUntil(this.surveyView$)
      .takeUntil(this.isSurveyCompleted$)
      .withLatestFrom(this.assessmentService.getSectionsProgress$())
      .map(([section, progress]) => {
        let left = parseInt(section.tempoEsecuzione)
          , viewTimeProgress = 0
          , viewTimeVolatile = this.assessmentService.getSectionViewTime(section.id);
        viewTimeVolatile = parseInt(viewTimeVolatile);

        if (typeof progress[section.id] !== 'undefined') {
          viewTimeProgress = parseInt(progress[section.id].viewTime || 0);
        }

        left = left - Math.max(viewTimeProgress, viewTimeVolatile);

        return { section: section, currentTime: left };
      })
      .switchMap(({ section, currentTime }) => this.utils
        .resettableCountDown$(this.triggerTimer$.mapTo(true), currentTime)
        .map(tick => ({ section: section, currentTime: currentTime - tick }))
        .startWith(({ section: section, currentTime: currentTime })));

    this.timeLeft$ = this.currentSectionTime$
      .map((currentSection) => {
        let execTime = parseInt(currentSection.section.tempoEsecuzione),
          currentTime = parseInt(currentSection.currentTime),
          viewTimeInSecs = execTime - currentTime
          ;
        if (viewTimeInSecs <= execTime) {
          this.assessmentService.updateSectionViewTime(
            currentSection.section.id,
            viewTimeInSecs
          );
        }
        return this.utils.mmssfromSecs(currentTime)
      });

    // force sync
    this.assessmentService.isStarted$().filter(isStarted => isStarted).take(1).subscribe(() => {
      let sectionsSync = {};
      this.currentSectionTime$
        .withLatestFrom(
          this.assessmentService.getCurrentSectionId$(),
          this.assessmentService.getRicetteMetadata$(),
          this.assessmentService.getNewSectionLoading$()
        )
        .subscribe(([x, currentSectionId, ricettaMetadata, newSectionLoading]) => {
          if (!sectionsSync[currentSectionId]) {
            sectionsSync[currentSectionId] = [];
            let lastSyncTime = 0;
            while (lastSyncTime < x.section.tempoEsecuzione) {
              lastSyncTime = lastSyncTime + SYNC_FREQUENCY_SECS;
              sectionsSync[currentSectionId].push(lastSyncTime);
            }
          }
          if (sectionsSync[currentSectionId].indexOf(x.currentTime) !== -1) {
            this.assessmentService.viewTimeSyncHandler(currentSectionId, ricettaMetadata, newSectionLoading, true);
          }
        });
    });

    this.assessmentService.getMetadata$()
      .subscribe((meta) => {
        this.shared['metaData'] = meta;
      });


    this.sectionTimeTerminated$ = this.currentSectionTime$
      //.do((x) => {console.log("sectionTimeTerminated$ - ");console.log(x)})
      .takeUntil(this.completed$.filter(complete => complete))
      .filter(({ currentTime }) => currentTime <= 0)
      .filter(() => !this.freeInsert);

    this.timePercentage$ = this.currentSectionTime$
      .map(({ section, currentTime }) => {
        let percentage = ((section.tempoEsecuzione - currentTime) * 100) / section.tempoEsecuzione;
        return percentage;
      });

    this.interruptExit$ = Observable.merge(
      this.pageEventsHandler.isUserOnPage$(),
      this.pageEventsHandler.mouseIn$)
      .filter(stay => stay);

    this.exitFullscreen$ = this.viewService
      .isFullscreen$()
      .switchMap(isFullscreen => !isFullscreen
        ? Observable.never()
        : Observable.merge(
          this.pageEventsHandler.isUserOnPage$()
            .filter(() => this.requestFullscreen && FULLSCREEN_EXIT_ON_LOST_FOCUS),
          this.pageEventsHandler.mouseIn$
            .filter(() => this.requestFullscreen && FULLSCREEN_EXIT_ON_MOUSE_OUT)
            .debounce(() => Observable.timer(MOUSE_OUT_ALLOWED_SECS * 1000)))
          .filter(stay => !stay));

    this.freezeView$ = Observable.merge(
      this.interruptExit$,
      Observable.merge(
        this.pageEventsHandler.mouseIn$,
        this.pageEventsHandler.isUserOnPage$())
        .filter(stay => !stay))
      .debounceTime(250);

    this.canShowPulse$ = this.freezeView$
      .switchMap(isInPage => isInPage
        ? Observable.of(!isInPage)
        : Observable.of(!isInPage)
          .delay(MOUSE_OUT_ALLOWED_WIHOUT_PULSE_SECS * 1000));
  }

  ngOnInit() {
    let queryParams = new URLSearchParams(window.location.search);
    let queryId = queryParams.get('?id');

    if (this.trainingMode && queryId) {
      this.store.dispatch(this.userActions.autoLogin(queryId));
    }

    Observable.combineLatest(
      this.userService.isLogged$(),
      this.assessmentService.isCompleted$(),
      this.assessmentService.isLocked$())
      .map(([logged, end, lock]) => lock || (!logged || (logged && end)))
      .filter(() => CHECK_BEFORE_CLOSE)
      .subscribe(canClose => this.canClose = canClose);

    this.store.dispatch(this.viewActions.changeView(
      this.requestFullscreen
        ? 'wait' : 'login'
    ));

    this.socketHandler.connect$
      .withLatestFrom(this.alerts$)
      .takeUntil(this.destroyed$)
      .subscribe(([, alerts]) => {
        for (let i = 0; i < alerts.length; i++) {
          if (alerts[i].body === this.translations['global#no_connection']) {
            this.store.dispatch(this.viewActions.removeAlert(alerts[i].id));
          }
        }
      });

    this.assessmentService.isLocked$()
      .filter(locked => locked)
      .takeUntil(this.destroyed$)
      .subscribe(() => this.socketHandler.guiEventRequest('test#locked'));

    this.translations$
      .takeUntil(this.destroyed$)
      .subscribe(translations => this.translations = translations);

    this.userData$
      .takeUntil(this.destroyed$)
      .subscribe(user => {
        this.shared['userData'] = user;
      });

    Observable.merge(
      this.pageEventsHandler.resizeInFullscreenMode$(RESIZE_TOLERANCE_IN_PX)
        .filter(() => this.requestFullscreen && FULLSCREEN_EXIT_ON_RESIZE)
        .mapTo(false),
      this.exitFullscreen$
        .delayWhen(() => Observable
          .timer(FULLSCREEN_EXIT_IN_SECS * 1000)
          .takeUntil(this.interruptExit$)))
      .filter(() => {
        let closeRequest = this.closeRequest;
        this.closeRequest = false;
        return !closeRequest;
      })
      .skipUntil(this.userService.isLogged$()
        .filter(isLogged => isLogged))
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.pageEventsHandler.exitFullscreen();
        this.store.dispatch(this.viewActions.closeModal());
      });

    this.pageEventsHandler.fullscreen$
      .takeUntil(this.destroyed$)
      .subscribe(isFullscreen => {
        this.store.dispatch(this.viewActions.setFullscreen(isFullscreen));
        this.isFullscreen = isFullscreen;

        if (!this.isFullscreen) {
          this.showHelp = false;
        }
      });

    this.completed$
      .takeUntil(this.destroyed$)
      .subscribe(completed => this.completed = completed);

    this.surveyView$
      .switchMap(inSurvey => !inSurvey
        ? Observable.never()
        : this.sectionTimeTerminated$
          .withLatestFrom(this.assessmentService.getRicetteMetadata$()))
      .takeUntil(this.destroyed$)
      .subscribe(([{ section }, ricette]) => {
        let currentIndex = ricette
          .map(ricetta => ricetta.id)
          .indexOf(section.id);
        let nextSection = ricette[currentIndex + 1];

        this.socketHandler.guiEventRequest('test#sectionTimeTerminated');

        if (nextSection) {
          this.store.dispatch(this.viewActions.openModal({
            title: this.translations['modal#termiate_title'],
            submitText: this.translations['modal#termiate_submit'],
            description: `
              ${this.translations['modal#termiate_description']} 
              <strong>${nextSection.nome}</strong>.
            `,
            isWarning: true,
            preventClose: true,
            action: this.assessmentActions.requestNewSectionBefore(nextSection.id)
          }));
        } else {
          this.store.dispatch(this.assessmentActions.interruptAttemp());

          this.store.dispatch(this.viewActions.openModal({
            title: this.translations['modal#terminate_request_title'],
            description: this.translations['modal#terminate_request_description'],
            submitText: this.translations['modal#terminate_request_submit'],
            checks: [
              this.translations['modal#terminate_request_check']
            ],
            preventClose: true,
            isWarning: true,
            action: this.assessmentActions.requestTerminateTest()
          }));
        }
      });

    this.socketHandler.connect$
      .takeUntil(this.destroyed$)
      .subscribe(() => this.isConnectedToServer = true);
    this.socketHandler.disconnect$
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.isConnectedToServer = false
      });

    this.socketHandler.message$
      .filter(m => m.type === 'force.disconnect.duplicate')
      .subscribe(() => {
        this.store.dispatch(this.viewActions.openModal({
          title: this.translations['console#duplicate_login_title'],
          description: this.translations['console#duplicate_login_description'],
          preventClose: true
        }));
      });

    Observable
      .fromEvent(window, 'keydown')
      .filter(() => document.activeElement.tagName.toLowerCase() !== 'input')
      .withLatestFrom(<any>this.viewService.getModal$())
      .filter(([, currentModal]) => typeof currentModal.content['isConsole'] === 'undefined')
      .takeUntil(this.destroyed$)
      .subscribe(([event, currentModal]) => {
        let validationsList = []
          , allValidations = {
            server: {
              type: this.translations['console#server_ok'],
              value: this.isConnectedToServer
            },
            internet: {
              type: this.translations['console#internet_ok'],
              value: true
            },
            canvas: {
              type: this.translations['console#canvas_ok'],
              value: Modernizr.canvas
            },
            html5: {
              type: this.translations['console#html5_ok'],
              //value: Modernizr.customelements
              value: document.createElement('canvas').getContext != undefined
            },
            audio: {
              type: this.translations['console#audio_ok'],
              value: Modernizr.audio && Modernizr.audio.mp3
            },
            storage: {
              type: this.translations['console#storage_ok'],
              value: Modernizr.localstorage
            },
            websockets: {
              type: this.translations['console#websockets_ok'],
              value: Modernizr.localstorage
            },
            pingCisia: {
              type: this.translations['console#ping_cisia_ok'],
              value: false,
              manualCheck: true
            },
            speedTest: {
              type: this.translations['console#speed_test'],
              value: false,
              manualCheck: true
            },
            sendLog: {
              type: this.translations['console#send_log'],
              value: false,
              manualCheck: true
            }
          };

        let openConsoleModal = (validations) => {
          for (let p in this.partsToCheck) {
            if (this.partsToCheck[p] && validations[p]) {
              validations[p].key = p;
              validationsList.push(validations[p]);
            }
          }

          this.store.dispatch(this.viewActions.openModal({
            title: this.translations['console#title'],
            submitText: this.translations['console#close'],
            action: currentModal.active
              ? this.viewActions.openModal(currentModal.content)
              : this.viewActions.closeModal(),
            validationsList: validationsList,
            appVersion: "V. " + CS_F_VERSION,
            preventClose: true,
            isConsole: true
          }));
        };

        this.currentCombo.shift();
        this.currentCombo.push(event['which']);

        if (!this.currentCombo.some((e, i) => e !== this.combo[i])) {
          this.store.dispatch(this.viewActions.openModal({
            title: this.translations['console#title'],
            description: 'Controlli in corso...'
          }));

          Observable.merge(
            Observable.of(null).delay(EXTERNAL_URL_TO_CHECK_CONNECTION_TIMEOUT),
            this.utils.get$(EXTERNAL_URL_TO_CHECK_CONNECTION + '?' + new Date().getTime())
          )
          .take(1)
          .subscribe(
            (v) => {
              allValidations.internet.value = v === null;
              openConsoleModal(allValidations);
            },
            (err) => {
              openConsoleModal(allValidations)
            });
        }
      });

    if (CHECK_DEV_TOOLS &&
      this.pageEventsHandler.areDevToolsOpen()) {
      console.error('DevTools opened');
    }

    if (DISABLE_RIGHT_CLICK) {
      this.pageEventsHandler.disableRightClick();
    }

    if (DISABLE_SHORTCUT) {
      this.pageEventsHandler.disableBrowserShortcuts();
    }

    if (DISABLE_CONSOLE) {
      this.pageEventsHandler.disableConsole();
    }

    if (DISABLE_SELECTION) {
      this.selectionHandler.disableSelect(document);
    }
  }

  _updateZoomStyle() {
    let body;
    if (navigator.userAgent.indexOf('Firefox') > 0) {
      body = document.getElementsByTagName('body')[0];
      Object.assign(body.style, {
        '-moz-transform-origin': '0 0',
        '-moz-transform': 'scale(' + (this.zoom / 100) + ')'
      });
    }
  }

  onExpandImage(image) {
    this.store.dispatch(this.viewActions.openModalImage({
      image: image
    }));
  }

  onModalImageClose() {
    this.store.dispatch(this.viewActions.closeModalImage());
  }

  onOpenHelp() {
    this.showHelp = true;
    this.socketHandler.guiEventRequest('help#open');
  }

  onCloseHelp() {
    this.showHelp = false;
  }

  onAlertClose(alertId) {
    this.store.dispatch(this.viewActions.removeAlert(alertId));
  }

  onSubmitRequest(request) {
    if (request.action) {
      this.store.dispatch(request.action);
    }
  }

  onSubmitValidation(request) {
    if (request.action &&
      request.validationCode === this.shared['userData'].birthday
        .replace(/-/g, '').split('').reverse().join('')) {
      this.store.dispatch(request.action);
    } else {
      this.store.dispatch(this.viewActions.addAlert({
        body: this.translations['survey#wrong_code_on_terminate'],
        type: 'danger'
      }));
    }
  }

  onHighContrastToggle() {
    // this.invertColor = !this.invertColor;
    this.invertStyle = !this.invertStyle;

    if (this.invertStyle) {
      // *:-webkit-full-screen, *:-webkit-full-screen * {
      //   -webkit-filter: none !important;
      //  }
      let head = document.getElementsByTagName('head')[0]
        , stl = document.createElement('style')
        , webFilterH = 'invert(100%) hue-rotate(180deg) brightness(110%) grayscale(20%) sepia(20%)'
        , css = `@media screen {
          html {
            -webkit-filter: ${webFilterH} !important;
          }
          img:not(.mwe-math-fallback-image-inline), iframe, video, embed {
            -webkit-filter: invert(100%) hue-rotate(180deg) !important;
            filter: hue-rotate(180deg) !important;
          }
          html {
            text-shadow: 0 0 0 !important;
          }
          html {
            background: rgb(0,0,0) !important;
          }
        }`;

      stl.setAttribute('type', 'text/css');
      stl.setAttribute('id', 'custom-invert');
      if (stl['styleSheet']) {
        stl['styleSheet'].cssText = css;
      } else {
        stl.appendChild(document.createTextNode(css));
      }
      head.appendChild(stl);
    } else {
      document.getElementById('custom-invert').remove();
    }
  }

  onCancelRequest() {
    this.store.dispatch(this.viewActions.closeModal());
  }

  onViewChange(name: string) {
    this.store.dispatch(this.viewActions.changeView(name));
  }

  increaseZoom() {
    const [limit, step] = this.utils.isMobile() ? [120, 10] : [150, 20];
    if (this.zoom < limit) {
      this.zoom += step;
      this._updateZoomStyle();
    }
  }

  decreaseZoom() {
    const [limit, step] = this.utils.isMobile() ? [80, 10] : [50, 20];
    if (this.zoom > limit) {
      this.zoom -= step;
      this._updateZoomStyle();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }
}




// WEBPACK FOOTER //
// ./src/app/app.component.ts